import { A4_WIDTH_PX, PRINTABLE_ROOT_ID } from "@components/Printable/PrintableRoot";
import { ActionIcon, Anchor, Group, Stack, Text } from "@mantine/core";
import {
  createDicomPerimetry,
  generateSessionPdfAsync,
  generateTestPdfAsync,
  postMethod,
} from "@utils/api-communication/RestApiCli";
import { type ReactNode, createElement, useCallback, useState } from "react";
import { deriveStudyInstanceUid, getDicomUidRoot } from "@utils/dicom/uid";

import { IconPrinter } from "@tabler/icons-react";
import { v4 as uuidv4 } from "uuid";
import { getAxiosInstance } from "@utils/axios-config/axios_config";
import i18next from "i18next";
import { Link } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { useSelector } from "react-redux";
import { selectVfaAdvancedMetricsEnabled } from "@store/userPreferences/userPreferencesSlice";
import { VFA_ADVANCED_METRICS_QUERY_FLAG } from "@utils/VFA/advancedMetricsScope";

type GenerateTrigger = "print" | "download" | "dicom" | "blob";

// `blob` is the background autosave path — no toast (would be noisy).
const NOTIFICATION_TITLES: Record<
  Exclude<GenerateTrigger, "blob">,
  { start: string; ok: string; err: string }
> = {
  print: { start: "Preparing print…", ok: "Print ready", err: "Print failed" },
  download: {
    start: "Generating PDF…",
    ok: "PDF ready",
    err: "PDF generation failed",
  },
  dicom: {
    start: "Generating DICOM…",
    ok: "DICOM ready",
    err: "DICOM generation failed",
  },
};

function notifyStarted(trigger: GenerateTrigger, message: ReactNode): string | null {
  if (trigger === "blob") return null;
  const id = `pdf-${uuidv4()}`;
  notifications.show({
    id,
    title: NOTIFICATION_TITLES[trigger].start,
    message,
    loading: true,
    autoClose: false,
    withCloseButton: false,
  });
  return id;
}

function withDownloadSettingsLink(text: string): ReactNode {
  return createElement(
    Stack,
    { gap: 4, align: "flex-start" },
    createElement(Text, { size: "sm" }, text),
    createElement(
      Anchor,
      {
        size: "sm",
        renderRoot: (props: React.ComponentPropsWithoutRef<"a">) =>
          createElement(Link, { ...props, to: "/user-settings" }),
      },
      i18next.t("UserSettings.preferences.pdfFilename.changeSetting"),
    ),
  );
}

function notifySuccess(id: string | null, trigger: GenerateTrigger): void {
  if (!id || trigger === "blob") return;
  notifications.update({
    id,
    title: NOTIFICATION_TITLES[trigger].ok,
    message: null,
    loading: false,
    color: "teal",
    autoClose: 2500,
    withCloseButton: true,
  });
}

function notifyError(id: string | null, trigger: GenerateTrigger, err: Error): void {
  if (trigger === "blob") return;
  const message = err.message || "Try again, or contact support if this keeps happening.";
  const title = NOTIFICATION_TITLES[trigger].err;
  if (!id) {
    notifications.show({ title, message, color: "red", autoClose: 5000 });
    return;
  }
  notifications.update({
    id,
    title,
    message,
    loading: false,
    color: "red",
    autoClose: 5000,
    withCloseButton: true,
  });
}

function notifyPrintReady(id: string | null, reprint: () => void): void {
  const message = createElement(
    Group,
    { gap: "xs", justify: "space-between", wrap: "nowrap" },
    createElement(Text, { size: "sm" }, "Didn't open? Print again:"),
    createElement(
      ActionIcon,
      {
        variant: "light",
        color: "teal",
        onClick: reprint,
        title: "Print",
        "aria-label": "Print",
      },
      createElement(IconPrinter, { size: 18 }),
    ),
  );
  const opts = {
    title: NOTIFICATION_TITLES.print.ok,
    message,
    loading: false,
    color: "teal" as const,
    autoClose: 10000,
    withCloseButton: true,
  };
  if (!id) {
    notifications.show(opts);
    return;
  }
  notifications.update({ id, ...opts });
}

interface UsePrintableOptions {
  filename?: string;
  width?: number;
  height?: number;
  landscape?: boolean;
  rootWidthOverride?: number;
  /**
   * If provided, the print/download/blob triggers route through the new
   * backend-driven endpoint POST /pdf/test/:uuid instead of building a
   * self-contained HTML blob in the browser. Much faster and avoids uploading
   * a multi-MB payload. Pass an array of test UUIDs (single or multi-test
   * page); a comma-separated string is also accepted.
   */
  testUuids?: string[] | string | null;
  sessionId?: string | null;
  /**
   * Extra query parameters forwarded to the UI print route (and the backend's
   * renderTest endpoint). Use this for the same flags `/test-result` already
   * accepts (summary, vision, lazyeye, neurology, cognitive, coverTestUuid,
   * visualAcuityUuid, etc.) so the print page reproduces the on-screen view.
   * Ignored when sessionId is set (the print route derives them from the session).
   */
  queryFlags?: Record<string, string | number | boolean | null | undefined>;
}

interface UsePrintableReturn {
  generate: (trigger?: GenerateTrigger, printOptions?: any) => Promise<void>;
  loading: boolean;
  error: Error | null;
}

interface DicomPrintOptions {
  filename: string;
  testId: string;
}

function normalizeTestUuids(input: string[] | string | null | undefined): string[] {
  if (!input) return [];
  const raw = Array.isArray(input) ? input.join(",") : input;
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeQueryFlags(
  flags: UsePrintableOptions["queryFlags"],
): Record<string, string> | undefined {
  if (!flags) return undefined;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(flags)) {
    if (value === undefined || value === null || value === "") continue;
    out[key] = String(value);
  }
  return Object.keys(out).length ? out : undefined;
}

export default function usePrintable({
  filename = "report.pdf",
  width = A4_WIDTH_PX,
  height,
  landscape = false,
  rootWidthOverride,
  testUuids,
  sessionId,
  queryFlags,
}: UsePrintableOptions = {}): UsePrintableReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const advancedMetricsEnabled = useSelector(selectVfaAdvancedMetricsEnabled);

  const generate = useCallback(
    async (trigger: GenerateTrigger = "download", printOptions?) => {
      setLoading(true);
      setError(null);

      const effectiveFilename = printOptions?.filename ?? filename;
      const decorateMessage = (text: string): ReactNode =>
        trigger === "download" && printOptions?.showDownloadSettingsLink === true
          ? withDownloadSettingsLink(text)
          : text;
      const notificationId = notifyStarted(trigger, decorateMessage(effectiveFilename));

      const finishOk = (): void => {
        notifySuccess(notificationId, trigger);
        setLoading(false);
      };
      const finishErr = (err: unknown): void => {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        console.error("[usePrintable]", e);
        notifyError(notificationId, trigger, e);
        // The blob trigger has no toast (background autosave) but callers
        // still need to know the request failed — surface via onError.
        if (trigger === "blob" && typeof printOptions?.onError === "function") {
          try {
            printOptions.onError(e);
          } catch (cbErr) {
            console.error("[usePrintable] onError callback failed", cbErr);
          }
        }
        setLoading(false);
      };

      // Dispatch a returned PDF blob to whatever the trigger requested.
      // Shared between the fast (/pdf/test/:uuid) and legacy (/pdf/generate)
      // paths; the only thing the paths differ on is how they get the blob.
      const dispatchPdfBlob = (responseData: ArrayBuffer): void => {
        const blob = new Blob([responseData], { type: "application/pdf" });
        if (trigger === "download") triggerDownload(blob, effectiveFilename);
        if (trigger === "print") {
          const { reprint } = triggerPrint(blob);
          notifyPrintReady(notificationId, reprint);
          setLoading(false);
          return;
        }
        if (trigger === "blob" && typeof printOptions?.onBlob === "function") {
          try {
            printOptions.onBlob(blob);
          } catch (cbError) {
            console.error("[usePrintable] onBlob callback failed", cbError);
          }
        }
        finishOk();
      };

      const axiosOpts = { responseType: "arraybuffer" as const, timeout: 180000 };

      try {
        if (trigger === "dicom") {
          if (!isDicomPrintOptions(printOptions)) {
            throw new Error("DICOM generation requires filename and testId options");
          }
          triggerDicom(printOptions).then(finishOk).catch(finishErr);
          return;
        }

        const effectiveLandscape =
          typeof printOptions?.landscape === "boolean" ? printOptions.landscape : landscape;
        const effectiveTestUuids = normalizeTestUuids(printOptions?.testUuids ?? testUuids);
        const effectiveQueryFlags = normalizeQueryFlags(printOptions?.queryFlags ?? queryFlags);
        const effectiveSessionId = printOptions?.sessionId ?? sessionId;

        // Fast path: backend drives a headless browser against /print/test.
        // Section-scoped prints stay on the legacy path — no section
        // primitive in the new endpoint yet.
        const canUseSessionRender = !printOptions?.section && !!effectiveSessionId;
        const canUseBackendRender =
          !printOptions?.section && !canUseSessionRender && effectiveTestUuids.length > 0;

        if (canUseSessionRender || canUseBackendRender) {
          // headless print page has no stored language or preferences, forward
          // them via query flags.
          const lng = i18next.language;
          const preferenceFlags = {
            [VFA_ADVANCED_METRICS_QUERY_FLAG]: advancedMetricsEnabled ? "true" : "false",
          };
          const backendQueryFlags = canUseSessionRender
            ? { ...(lng ? { lng } : {}), ...preferenceFlags }
            : { ...(lng ? { lng } : {}), ...(effectiveQueryFlags ?? {}), ...preferenceFlags };
          const body = {
            filename: effectiveFilename,
            ...(effectiveLandscape ? { landscape: true } : {}),
            ...(Object.keys(backendQueryFlags).length ? { queryFlags: backendQueryFlags } : {}),
          };
          const onStatusChange = (message: string): void => {
            if (notificationId && message) {
              notifications.update({ id: notificationId, message: decorateMessage(message) });
            }
          };
          const request = canUseSessionRender
            ? generateSessionPdfAsync(effectiveSessionId as string, body, { onStatusChange })
            : generateTestPdfAsync(encodeURIComponent(effectiveTestUuids.join(",")), body, {
                onStatusChange,
              });
          request.then((arrayBuffer) => dispatchPdfBlob(arrayBuffer)).catch(finishErr);
          return;
        }

        const html = await buildPrintHtml(printOptions?.section, {
          landscape: effectiveLandscape,
          rootWidthOverride,
        });
        postMethod(
          "/pdf/generate",
          {},
          {
            html,
            filename: effectiveFilename,
            width,
            ...(height ? { height } : {}),
            ...(effectiveLandscape ? { landscape: true } : {}),
          },
          (response) => dispatchPdfBlob(response.data),
          finishErr,
          undefined,
          getAxiosInstance(),
          axiosOpts,
        );
      } catch (error) {
        finishErr(error);
      }
    },
    [
      filename,
      width,
      height,
      landscape,
      rootWidthOverride,
      testUuids,
      sessionId,
      queryFlags,
      advancedMetricsEnabled,
    ],
  );

  return { generate, loading, error };
}

const PAIR_ATTR = "data-print-pair-id";

async function buildPrintHtml(
  section?: HTMLElement,
  opts: { landscape?: boolean; rootWidthOverride?: number } = {},
): Promise<string> {
  const baseUrl = document.baseURI || window.location.href;

  const targetElement = document.getElementById(PRINTABLE_ROOT_ID);
  if (!targetElement) {
    throw new Error(`Element with id "${PRINTABLE_ROOT_ID}" not found`);
  }

  const liveBgEntries = collectLiveBackgroundImages(targetElement);
  liveBgEntries.forEach((entry, i) => entry.element.setAttribute(PAIR_ATTR, String(i)));

  try {
    const cloned = targetElement.cloneNode(true) as HTMLElement;

    if (opts.rootWidthOverride) {
      cloned.style.width = `${opts.rootWidthOverride}px`;
      cloned.style.minHeight = "0";
      cloned.style.overflow = "hidden";
    }

    cloned.querySelectorAll<HTMLElement>("[data-print-zoom]").forEach((el) => {
      const printZoom = el.getAttribute("data-print-zoom");
      if (printZoom) {
        el.style.zoom = printZoom;
      }
    });

    const liveCanvases = targetElement.querySelectorAll("canvas");
    const clonedCanvases = cloned.querySelectorAll("canvas");

    liveCanvases.forEach((liveCanvas, index) => {
      const dataUrl = liveCanvas.toDataURL("image/png");
      const img = document.createElement("img");
      img.src = dataUrl;
      img.width = liveCanvas.offsetWidth;
      img.height = liveCanvas.offsetHeight;
      img.style.cssText = window.getComputedStyle(liveCanvas).cssText;
      clonedCanvases[index].replaceWith(img);
    });

    if (section) {
      const liveSections = Array.from(targetElement.querySelectorAll(".printable-section"));
      const sectionIndex = liveSections.indexOf(section);
      // A result whose sections share a print group prints as a whole: the
      // print button sits in one of them, but the others are the same result.
      const group = section.getAttribute("data-print-group");
      const keptIndexes = new Set(
        group
          ? liveSections.reduce<number[]>((indexes, el, i) => {
              if (el.getAttribute("data-print-group") === group) indexes.push(i);
              return indexes;
            }, [])
          : [sectionIndex],
      );
      Array.from(cloned.querySelectorAll(".printable-section")).forEach((el, i) => {
        if (!keptIndexes.has(i)) {
          el.remove();
        }
      });
    }

    await inlineClonedImages(cloned);
    await inlineClonedBackgroundImages(cloned, liveBgEntries);

    if (cloned.hasAttribute(PAIR_ATTR)) cloned.removeAttribute(PAIR_ATTR);
    cloned.querySelectorAll(`[${PAIR_ATTR}]`).forEach((el) => el.removeAttribute(PAIR_ATTR));

    const inlinedStyles = filterCssRules(cloned);

    const htmlAttrs = Array.from(document.documentElement.attributes)
      .map((attr) =>
        attr.name === "data-mantine-color-scheme"
          ? `${attr.name}="light"`
          : `${attr.name}="${attr.value}"`,
      )
      .join(" ");

    const existingBodyClass = document.body.getAttribute("class") ?? "";
    const mergedBodyClass = `${existingBodyClass} pdf-render-mode`.trim();
    const bodyAttrsWithoutClass = Array.from(document.body.attributes)
      .filter((attr) => attr.name !== "class")
      .map((attr) => `${attr.name}="${attr.value}"`)
      .join(" ");
    const bodyAttrsFinal = `${bodyAttrsWithoutClass} class="${mergedBodyClass}"`.trim();

    const fullHtml = `<!DOCTYPE html>
      <html ${htmlAttrs}>
        <head>
          <base href="${baseUrl}" />
          <meta charset="UTF-8" />
          ${inlinedStyles}
          <style>
            @page { ${opts.landscape ? "size: landscape; " : ""}margin: 0; }
            html, body { margin: 0; padding: 0; }
            .no-print { display: none !important; }
            #chatbase-bubble-button { display: none !important; }
            #chatbase-message-bubbles { display: none !important; }
          </style>
        </head>
        <body ${bodyAttrsFinal}>
          ${cloned.outerHTML}
        </body>
      </html>`;
    return fullHtml;
  } finally {
    liveBgEntries.forEach((entry) => entry.element.removeAttribute(PAIR_ATTR));
  }
}

function filterCssRules(clonedRoot: HTMLElement): string {
  const usedClasses = new Set<string>();
  const allClonedEls = [clonedRoot, ...clonedRoot.querySelectorAll<HTMLElement>("*")];
  for (const el of allClonedEls) {
    el.classList.forEach((c) => usedClasses.add(c));
  }

  const out: string[] = [];

  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRule[];
    try {
      rules = Array.from(sheet.cssRules);
    } catch {
      if (sheet.ownerNode instanceof Element) {
        out.push(sheet.ownerNode.outerHTML);
      }
      continue;
    }

    const kept: string[] = [];
    for (const rule of rules) {
      if (ruleMatchesUsage(rule, usedClasses)) {
        kept.push(rule.cssText);
      }
    }
    if (kept.length) {
      out.push(`<style>${kept.join("\n")}</style>`);
    }
  }

  return out.join("\n");
}

function ruleMatchesUsage(rule: CSSRule, classes: Set<string>): boolean {
  if (rule instanceof CSSStyleRule) {
    return selectorReferencesUsedClasses(rule.selectorText, classes);
  }
  if (rule instanceof CSSMediaRule || rule instanceof CSSSupportsRule) {
    return Array.from(rule.cssRules).some((r) => ruleMatchesUsage(r, classes));
  }
  return true;
}

function selectorReferencesUsedClasses(selectorText: string, classes: Set<string>): boolean {
  if (/:(?:not|where|is|has)\(/.test(selectorText)) return true;

  return selectorText.split(",").some((part) => {
    const refs = Array.from(part.matchAll(/\.([\w-]+)/g)).map((m) => m[1]);
    if (refs.length === 0) return true;
    return refs.every((c) => classes.has(c));
  });
}

interface LiveBgEntry {
  element: HTMLElement;
  backgroundImage: string;
}

function collectLiveBackgroundImages(root: HTMLElement): LiveBgEntry[] {
  const result: LiveBgEntry[] = [];
  const all = [root, ...Array.from(root.querySelectorAll<HTMLElement>("*"))];
  for (const el of all) {
    const bg = window.getComputedStyle(el).backgroundImage;
    if (bg && bg !== "none") {
      result.push({ element: el, backgroundImage: bg });
    }
  }
  return result;
}

async function inlineClonedImages(clonedRoot: HTMLElement): Promise<void> {
  const clonedImages = Array.from(clonedRoot.querySelectorAll("img"));

  const cache = new Map<string, Promise<string>>();
  const resolve = (src: string): Promise<string> => {
    let pending = cache.get(src);
    if (!pending) {
      pending = convertUrlToDataUrl(src);
      cache.set(src, pending);
    }
    return pending;
  };

  await Promise.all(
    clonedImages.map(async (clonedImage) => {
      const src = clonedImage.currentSrc || clonedImage.getAttribute("src");
      if (!src) {
        return;
      }

      try {
        clonedImage.setAttribute("src", await resolve(src));
        clonedImage.removeAttribute("srcset");
      } catch {
        clonedImage.setAttribute("src", src);
      }
    }),
  );
}

async function inlineClonedBackgroundImages(
  clonedRoot: HTMLElement,
  liveBgEntries: LiveBgEntry[],
): Promise<void> {
  const bgByPairId = new Map(liveBgEntries.map((entry, i) => [String(i), entry.backgroundImage]));

  const targets: Array<{ element: HTMLElement; backgroundImage: string }> = [];

  const tagged: HTMLElement[] = clonedRoot.hasAttribute(PAIR_ATTR) ? [clonedRoot] : [];
  clonedRoot.querySelectorAll<HTMLElement>(`[${PAIR_ATTR}]`).forEach((el) => tagged.push(el));

  tagged.forEach((el) => {
    const bg = bgByPairId.get(el.getAttribute(PAIR_ATTR)!);
    if (bg) targets.push({ element: el, backgroundImage: bg });
  });

  await Promise.all(
    targets.map(async ({ element, backgroundImage }) => {
      const matches = Array.from(backgroundImage.matchAll(/url\((['"]?)(.*?)\1\)/g));
      if (matches.length === 0) return;

      let nextBackgroundImage = backgroundImage;
      for (const match of matches) {
        const [, , url] = match;
        try {
          const dataUrl = await convertUrlToDataUrl(url);
          nextBackgroundImage = nextBackgroundImage.replace(match[0], `url("${dataUrl}")`);
        } catch {}
      }

      element.style.backgroundImage = nextBackgroundImage;
    }),
  );
}

async function convertUrlToDataUrl(url: string): Promise<string> {
  if (url.startsWith("data:") || url.startsWith("blob:")) {
    return url;
  }

  const absoluteUrl = new URL(url, document.baseURI || window.location.href).toString();
  const parsedUrl = new URL(absoluteUrl);
  if (parsedUrl.origin !== window.location.origin) {
    throw new Error(`Skipping cross-origin asset: ${absoluteUrl}`);
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch(absoluteUrl, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`Failed to fetch asset: ${absoluteUrl}`);
    }

    const blob = await response.blob();
    return await blobToDataUrl(blob);
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Failed to convert blob to data URL"));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read blob"));
    reader.readAsDataURL(blob);
  });
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function triggerPrint(blob: Blob): { reprint: () => void; cleanup: () => void } {
  const url = URL.createObjectURL(blob);
  let disposed = false;

  const printOnce = (): void => {
    if (disposed) return;
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";

    let removed = false;
    const removeIframe = (): void => {
      if (removed) return;
      removed = true;
      iframe.parentNode?.removeChild(iframe);
    };

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.addEventListener("afterprint", () =>
          window.setTimeout(removeIframe, 0),
        );
        iframe.contentWindow?.print();
      } catch (e) {
        console.warn("[usePrintable] iframe print failed", e);
      }
      window.setTimeout(removeIframe, 60_000);
    };

    iframe.src = url;
    document.body.appendChild(iframe);
  };

  printOnce();

  const cleanup = (): void => {
    if (disposed) return;
    disposed = true;
    URL.revokeObjectURL(url);
  };
  window.setTimeout(cleanup, 300_000);

  return { reprint: printOnce, cleanup };
}

async function triggerDicom(options: DicomPrintOptions): Promise<void> {
  const uidRoot = getDicomUidRoot();
  const uids = { studyInstanceUid: deriveStudyInstanceUid(uidRoot), uidRoot };

  const [opvBuffer, pdfBuffer] = await Promise.all([
    createDicomPerimetry(options.testId, "opv", { uids }),
    createDicomPerimetry(options.testId, "encapsulated-pdf", { uids }),
  ]);

  const baseName = options.filename.replace(/\.[^./\\]+$/, "");
  triggerDownload(new Blob([opvBuffer], { type: "application/dicom" }), `${baseName}-OPV.dcm`);
  triggerDownload(new Blob([pdfBuffer], { type: "application/dicom" }), `${baseName}-PDF.dcm`);
}

function isDicomPrintOptions(printOptions: unknown): printOptions is DicomPrintOptions {
  if (!printOptions || typeof printOptions !== "object") {
    return false;
  }

  return (
    "filename" in printOptions &&
    typeof printOptions.filename === "string" &&
    "testId" in printOptions &&
    typeof printOptions.testId === "string"
  );
}
